/*!
// <copyright file="ListRenderer.js" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

/// <reference path="../html-binding-controls.ts" />
/// <reference path="../html-binding-handlers.ts" />
/// <reference path="Grid.js" />
(function () {
    'use strict';

    // Imports
    // MS Internal use only api
    var selector = $dyn.ui.selector;

    /**
    * @memberof $dyn.controls.List.Renderer
    * @typedef RenderedRow
    * @summary Represents cached version of the rendered row in the dom
    * @private
    *
    * @prop {Element} element
    * @prop {Number} version
    * @prop {Boolean} selected
    * @prop {String} type
    * @prop {Boolean} marked
    * @prop {Number} id
    * @prop {Number} order
    */

    /**
    * @class Renderer
    * @classdesc The Renderer component of the List control
    * @summary Responsible for managing the DOM state of the grid
    * @internal
    * @memberof $dyn.controls.List
    * @param {Object} listContext - Reference to List object that owns Renderer
    * @param {Object} options - Input renderer related external properties.
    */
    $dyn.controls.List.Renderer = function (listContext/*Should be some props*/, options) {
        var that = this;

        this._listContext = listContext;
        this._dataProvider = options.dataProvider;
        this._syncEngineState = options.syncEngineState;
        this._listBody = options.listBody;
        this._invalidRow = options.invalidRow;
        this._renderForeachTask = $dyn.bindingHandlers.foreach.task;
        this._itemTask = $dyn.bindingHandlers.foreach.itemTask;
        this._rowIdToRenderedRow = {};
        this._rowIds = [];
        this._userActionBusy = $dyn.observable(false);
        this._userActionBusyRenderPending = $dyn.observable(false);
        this.userNotBusyAndSyncInactive = $dyn.cachedComputed(this._userNotBusyAndSyncInactive, null, this);
        this._disposables = [];
        this.rowSyncState = options.rowSyncState;
        this._rowHasPendingEdit = options.rowHasPendingEdit;
        this._markAllRecords = options.markAllRecords;
        if (this._listContext.Root) {
            this._disposables.push($dyn.observe(this._listContext.Root.ActiveControl, function (activeControl) {
                // Synchronize active cell to active control if fast editing is not allowed or current active cell value is empty
                if (!that._listContext._fastEditingAllowed()) {
                    that._syncActiveCellToActiveControl();
                }
            }));
        }
        this._disposables.push($dyn.observe(this._invalidRow, this._invalidRowChanged, this));
        //Observes when MarkAllRecords is changed from server
        this._disposables.push($dyn.observe(this._markAllRecords, this._setAllMarkedRows, this));
        this._onRowBecameCommittedSubscription = this._dataProvider.onRowBecameCommitted.subscribe(this._onRowBecameCommitted, this);
    };

    $dyn.controls.List.Renderer.prototype = {
        /**
        * @member
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Represents the name of the active cell that should be focused in the grid
        * @type {String}
        */
        _activeCell: null,

        /**
        * @member
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Represents if the markAllRecords has changed
        * @type {Object}
        */
        _markAllRecords: false,

        /**
        * @member
        * @memberof $dyn.controls.List.Renderer#
        * @public
        * @summary Represents if the mark or unmark checkbox is clicked
        * @type {Object}
        */
        allRecordsMarkTriggered: false,
        /**
        * @member
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary The data provider component instance
        * @type {$dyn.controls.List.DataProvider}
        */
        _dataProvider: null,

        /**
        * @member
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary An array of rendered row ids
        * @type {Array}
        */
        _rowIds: null,
        /**
        * @member
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary A mapping from rendered row id to rendered row
        * @type {Object}
        */
        _rowIdToRenderedRow: null,
        _listContext: null,
        _listBody: null,
        get _selectedRowId() {
            if (!this._activeRowInTheDOM()) {
                return null;
            } else {
                return this.getInternalRowIdByElement(this._listContext._selectedRow);
            }
        },

        userNotBusyAndSyncInactive: null,
        _selectingRowId: null,
        _inactiveRowElement: null,
        _activeRowElement: null,
        _syncEngineState: null,
        _invalidRow: null,
        _previousInvalidRow: null,
        _disposables: null,
        _placeholderRowId: "_placeholderRowId",
        _isInvalidClass: "is-invalid",
        _isLoadingClass: "is-loading",
        _rowSelecting: "data-dyn-row-selecting",
        _renderModeMerge: "merge",
        _renderModeReplace: "replace",
        _renderedGroupBy: false,
        _userActionBusyTimer: undefined,
        _throttleOnResize: null,
        _recalculateRowHeightWithinLayoutBlock: true,

        /**
        * @member
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Indicates user is busy performing some keyboard action (entering/navigating within the grid).
        * @description Flag is set to true on a RowKeyDown, and reset to false on a timer.
        * When grid is in ahead mode, this flag will ensure that New/Change row callbacks don't re-render the grid while the user is busy
        * either entering/navigating within the grid.
        * @type {Boolean|Observable}
        */
        _userActionBusy: null,
        _userActionBusyRenderPending: null,
        _fullRenderPostponed: false,
        _lastRenderMode: "replace",
        _isRendering: false,

        // Virtual function, it is supposed to be overriden by constructor
        _rowHasPendingEdit: function (rowId) {
            return false;
        },

        //Tasks
        _renderForeachTask: null,
        _itemTask: null,

        // Maintain the fixed length row height for items
        _rowHeight: null,
        //Array maintaining pending rows which are removed by renderer
        _deletedPendingRows: null,
        //Set to true when first row has pending row pivoted against it and gets deleted
        _firstRowDeleted: null,

        getRowHeight: function (listBody, recalculate) {
            if (!this._rowHeight || recalculate) {
                // TODO: In Chrome first row has less height than others. Would be great to investigate.
                var lastRow = $(listBody).find('.rowTemplate.listItem').last();
                // When the row element is not visible we still can have non 0 outer height that will be incorrect one
                // That's why we need to check if the offsetParent is null, it will be null if the control is not displayed
                if (lastRow.length && lastRow[0].offsetParent !== null && !$dyn.shell.isShellInitializing()) {
                    this._rowHeight = lastRow.outerHeight();
                }
            }
            return this._rowHeight;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Creates new row element that represents inactive row
        * @param {Object} item - Row data item
        * @returns {Element} Inactive row element
        */
        _createNewInactiveRowElement: function (item) {
            this._listContext._inactiveItem(item);
            // use jquery clone with (true 'copy deep', true 'copy events')
            var $element = $(this._listContext._inactiveItemElement).clone(true, true); 
            // loop element
            $element.find('[id]').each(function () {
                //Change the element id's to be unique
                this.id = this.id + "_row_" + item.id;
            });
            $element[0].dynRowItem = item;
            // change the actual row id to be unique so it doesnt conflict with the header row
            $element.attr('id', $element.attr('id') + "_row_" + item.id);
            $element.attr("data-dyn-row-active", false);
            return $element[0];
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Gets rendered row by its index
        * @param {Number} index - Index
        * @returns {$dyn.controls.List.Renderer.RenderedRow} Rendered row
        */
        _getRenderedRowByIndex: function (index) {
            return this._getRenderedRowById(this._rowIds[index]);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Gets rendered row by its order
        * @param {Number} rowId - Order
        * @returns {$dyn.controls.List.Renderer.RenderedRow} Rendered row
        */
        _getRenderedRowById: function (rowId) {
            return this._rowIdToRenderedRow[rowId];
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Gets first rendered row in the data source's page
        * @returns {$dyn.controls.List.Renderer.RenderedRow} Rendered row
        */
        _getFirstRenderedPageRow: function () {
            var dataCollection = $dyn.peek(this._dataProvider.Collection);
            for (var i = 0, len = dataCollection && dataCollection.length; i < len ; ++i) {
                var dataId = this._dataProvider.getDataRowIdByPageIndex(i);
                var internalId = this._dataProvider.getInternalRowId(dataId);
                if (this._rowIdToRenderedRow[internalId]) {
                    return this._rowIdToRenderedRow[internalId];
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Gets last rendered row in the data source's page
        * @returns {$dyn.controls.List.Renderer.RenderedRow} Rendered row
        */
        _getLastRenderedPageRow: function () {
            var dataCollection = $dyn.peek(this._dataProvider.Collection);
            for (var i = dataCollection && dataCollection.length - 1; i >= 0; --i) {
                var dataId = this._dataProvider.getDataRowIdByPageIndex(i);
                var internalId = this._dataProvider.getInternalRowId(dataId);
                if (this._rowIdToRenderedRow[internalId]) {
                    return this._rowIdToRenderedRow[internalId];
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @internal
        * @summary Indicates whether the row by given id is rendered within boundaries of backend active page or outside.
        * @param {Number} rowId - Row id
        * @returns {?Boolean} True if a row is within active page
        */
        isRowWithinActivePage: function (rowId) {
            var renderedRow = this._rowIdToRenderedRow[rowId];
            var pageStartRenderedRow = this._getFirstRenderedPageRow();
            var pageEndRenderedRow = this._getLastRenderedPageRow();
            if (renderedRow && pageStartRenderedRow && pageEndRenderedRow) {
                var renderedRowOrder = renderedRow.order;
                var pageStartOrder = pageStartRenderedRow.order;
                var pageEndOrder = pageEndRenderedRow.order;
                if (renderedRowOrder >= pageStartOrder && renderedRowOrder <= pageEndOrder) {
                    return true;
                } else {
                    return false;
                }
            }

            return undefined;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @public
        * @summary handles addition of row from Buttons and shortcuts
        * @param {targetRowElement} targetRowElement - row element.
        * @returns {Boolean} - true to stop propagation of the event
        */
        newRecord: function (targetRowElement) {
            var childElement = $dyn.util.findFirstFocusableControl(this._listContext.element);
            if ($dyn.peek(this._listContext._fastEdit) && targetRowElement) {
                this._activeCell(this._listContext._getParentControlName(childElement));
                var currentRowId = this.getInternalRowIdByElement(targetRowElement);
                this._dataProvider.createRow(currentRowId);
                return true;
            }

            return false;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @public
        * @summary handles deletion of row from Buttons and shortcuts
        * @param {targetRowElement} targetRowElement - row element.
        * @param {event} event - event of the delete action.
        * @returns {boolean?} - false if we don't want to stop propagation otherwise undefined
        */
        deleteRecord: function (targetRowElement, event) {
            //In a fast edit grid we can have pending rows. We need to maintain the pending rows so that we can put it back after deletion
            //_deletedPendingRows is a associative array with internal row id as key and rows as values
            var isFastEdit = $dyn.peek(this._listContext._fastEdit);
            if (isFastEdit) {
                this._deletedPendingRows = null;
                for (var i = 0; i < this.getRenderedRowCount() ; ++i) {
                    var item = this._getRenderedRowByIndex(i);
                    var itemId = item && item.id;
                    if (itemId) {
                        if (item.type !== this._dataProvider.committedRowType && item.id !== this._placeholderRowId) {
                            if (!this._deletedPendingRows) {
                                this._deletedPendingRows = {};
                            }
                            this._deletedPendingRows[item.id] = item;
                        }
                    }
                }
            }
            if (targetRowElement) {
                var rowId = this.getInternalRowIdByElement(targetRowElement);
                var handled = false;
                // First remove the row from sync manager's queue, if it didn't reject it, remove it from data provider
                if (this._listContext._syncManager.removeRowFromQueue(rowId) !== false) {
                    handled = this._dataProvider.deleteRow(rowId);
                }
                if (handled) {
                    event.preventDefault();
                    event.stopPropagation();
                } else {
                    // In case data provider hasn't handled the row deletion request we additionaly check for sync manager state being in default state
                    // In case sync manager is in error state or has non-empty queue, we prevent deletion of the row as current client DOM state may not match server state
                    // and wrong row may be deleted if deleting while being in the an 'error' state or while having some changes in sync manager queue.
                    //Stop propagation if the row is not active row.
                    if ((this._listContext._syncManager.inErrorState() || !this._listContext._syncManager.isQueueEmpty()) && !this._listContext._syncManager.isActiveRow(parseInt(rowId))) {
                        $dyn.log.list && $dyn.log.list("List - [Row Operations Manager]. Prevented row deletion on Alt+F9 or Alt+Delete. SyncManager state is: %s", this._listContext._syncManager.state);
                        event.preventDefault();
                        event.stopPropagation();
                    }
                    if (this._listContext._syncManager.isActiveRow(parseInt(rowId))) {
                        if (event.originalEvent) {
                            event.originalEvent.IgnoreShortcuts = false;
                        }
                        this._dataProvider.lastDeletedActiveRowId = rowId;
                        this._dataProvider.deleteTriggered = true;
                        if (this._listContext.Root && this._listContext.Root.MessageBar) {
                            // this is a temporary fix until Deliverable 1511094 and 331611 are complete
                            // The server will resend validation messages for data that wasn’t fixed so this is 
                            // safe until we complete Deliverable 1511094 and 331611
                            this._listContext.Root.MessageBar._clearWarningMessagesNotAttachedToInputControls();
                        }
                        return false;
                    }
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @internal
        * @summary Gets active page related index of the row by given id.
        * @description Renderer is able to calculate row index even if the row is outside of the active page
        * @param {Number} rowId - Row id
        * @returns {Number} Active page related index
        */
        getActivePageRelatedIndex: function (rowId) {
            var isRowWithinActivePage = this.isRowWithinActivePage(rowId);
            if (isRowWithinActivePage === true) {
                return this._dataProvider.getPageIndexByInternalRowId(rowId);
            }
            if (isRowWithinActivePage === false) {
                var renderedRow = this._rowIdToRenderedRow[rowId];
                var pageStartRenderedRow = this._getFirstRenderedPageRow();
                var pageEndRenderedRow = this._getLastRenderedPageRow();
                if (renderedRow && pageStartRenderedRow && pageEndRenderedRow) {
                    if (renderedRow.order < pageStartRenderedRow.order) {
                        // Row above page
                        // At this point we assume that all rows outside active page are existing rows on server
                        return renderedRow.order - pageStartRenderedRow.order;
                    }
                    if (renderedRow.order > pageEndRenderedRow.order) {
                        // Row below active page
                        // At this point we assume that all rows outside active page are existing rows on server
                        return this._dataProvider.getPageLength() - 1 + renderedRow.order - pageEndRenderedRow.order;
                    }
                }
            }

            // Row index wasn't found
            return undefined;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @internal
        * @summary Gets row id by its offset from the currently active row
        * @param {Number} offset - Offset from the currently active row
        * @returns {Number} RowId
        */
        getRowIdByOffset: function (offset) {
            var userSelectedId = $dyn.peek(this._dataProvider.InternalSelectedRowId);
            var renderedRow = this._rowIdToRenderedRow[userSelectedId];
            if (renderedRow) {
                var order = renderedRow.order;
                var targetOrder = order + offset;
                if (targetOrder >= 0 && targetOrder < this.getRenderedRowCount()) {
                    return this._rowIds[targetOrder];
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @internal
        * @summary Gets row id by its active page related index
        * @param {Number} pageIndex - Page index
        * @returns {Number} RowId
        */
        getRowIdByActivePageIndex: function (pageIndex) {
            if (pageIndex >= 0 && pageIndex < this._dataProvider.getPageLength()) {
                return this._dataProvider.getInternalRowIdByPageIndex(pageIndex);
            }

            if (pageIndex < 0) {
                var pageStartRenderedRow = this._getFirstRenderedPageRow();
                if (pageStartRenderedRow) {
                    var pageStartOrder = pageStartRenderedRow.order;
                    var targetRowOrder = pageStartOrder + pageIndex;
                    return this._rowIds[targetRowOrder];
                }
            } else {
                // We are calculating starting from page end because in between we can have pending rows
                var pageEndRenderedRow = this._getLastRenderedPageRow();
                if (pageEndRenderedRow) {
                    var targetRowOrder = pageIndex + 1 - this._dataProvider.getPageLength() + pageEndRenderedRow.order;
                    return this._rowIds[targetRowOrder];
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @internal
        * @summary Gets rendered row count
        * @returns {Number} Number of rendered rows
        */
        getRenderedRowCount: function () {
            return this._rowIds.length;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @internal
        * @summary Indicates whether grid is in the restricted rendering mode
        * @description When grid is in restricted rendering mode, rendering can only create or delete client pending rows
        * Restricted rendering mode is temporary
        * @returns {Boolean} True if the grid is in the restricted rendering mode
        */
        restrictedRenderingMode: function () {
            return !!$dyn.peek(this._userActionBusy);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Gets the committed row (that exists on back end) that is the last rendered row
        * @returns {$dyn.controls.List.Renderer.RenderedRow} Rendered rows
        */
        _getBottomCommittedRow: function () {
            for (var i = this._rowIds.length - 1; i >= 0; --i) {
                var renderedRow = this._rowIdToRenderedRow[this._rowIds[i]];
                if (renderedRow && renderedRow.type === this._dataProvider.committedRowType) {
                    return renderedRow;
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @internal
        * @summary Gets the active page related index of the bottom committed row
        * @returns {Number} Page index
        */
        getBottomRowPageIndex: function () {
            var bottomCommittedRow = this._getBottomCommittedRow();
            if (bottomCommittedRow) {
                return this.getActivePageRelatedIndex(bottomCommittedRow.id);
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @internal
        * @summary The functionality is responsible for handling external focus change.
        * @description In case the grid is not in the user busy mode or sync manager is inactive grid handles focus and rejects external focus handling.
        * Grid also rejects focus handling if the given element is not a child of the active row
        * @param {element} element - An element to focus
        * @param {forceUpdateActiveCell} forceUpdateActiveCell - Boolean if we need to update active cell before focus
        * @returns {Boolean} False if Grid handled the focus, True otherwise
        */
        tryFocus: function (element, forceUpdateActiveCell) {
            $dyn.log.focus && $dyn.log.focus("Entering grid:tryFocus");
            var shouldFocus = true;
            // If user is not busy and sync engine inactive we consider that user is not actively changing the focus
            if (this.userNotBusyAndSyncInactive() || forceUpdateActiveCell) {
                var userSelectedRow = this._listContext.AllowHeaderFocus() ? this._listContext._headerRowElement : this.getUserSelectedElement();
                if (userSelectedRow) {
                    var controlName = this._listContext._getParentControlName(element);
                    if (!$.contains(userSelectedRow, element)) {
                        // Do not allow to focus an element that is outside of user selected row.
                        shouldFocus = !!$(userSelectedRow).find('[data-dyn-controlname="' + controlName + '"]').length;
                    } else {
                        this._activeCell(controlName);
                    }
                }
            } else {
                this.focusCellInActiveRow();
                // We handled the focus
                shouldFocus = false;
            }
            $dyn.log.focus && $dyn.log.focus("Leaving grid:tryFocus");
            return shouldFocus;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Synchronizes Grid's activeCell to Form's activeControl if activeControl is within the grid.
        * Note: Only values are synchronized, the actual focus is not being transitioned
        */
        _syncActiveCellToActiveControl: function () {
            if (this._activeCell && this._listContext.Root) {
                var activeControl = $dyn.peek(this._listContext.Root.ActiveControl);
                var selectedRow = this._listContext._selectedRow;
                if (selectedRow && $(selectedRow).find('[data-dyn-controlname="' + activeControl + '"]').length > 0) {
                    this._activeCell(activeControl);
                } else {
                    this._activeCell("");
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary The getter for a computed 'userNotBusyAndSyncInactive' that is True when user is not busy and sync engine is not active
        * @returns {Boolean} True when user is not busy and sync engine is not active
        */
        _userNotBusyAndSyncInactive: function () {
            return !$dyn.value(this._userActionBusy) && $dyn.value(this._syncEngineState) === $dyn.util.SyncEngineState.inactive;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Runs disccard policy on the grid.
        * @description In case grid rendered an amount of rows that exceeeds predefined threshold (_RowCountThresholdFraction*visibleRowCount),
        * the rows are being discarded in the way that new amount of rows equals to 2 viewport sizes.
        * In general discard happens during infinite scroll.
        * Note: Right now _RowCountThresholdFraction is a constant. An improved can be made to have _RowCountThresholdFraction be dependent on grid performnance.
        * @param {Element} listBody - List body element
        * @returns {Boolean} The success of the operation
        */
        _runDiscardPolicy: function (listBody) {
            var that = this;
            if (this._listContext._disableDiscardPolicy === true) {
                return false;
            }

            if (this._listContext.clientCacheStart !== undefined && this._listContext.clientCacheStart !== null) {
                var rowHeight = this.getRowHeight(listBody);
                var scrollRegionHeight = this._listContext.GetScrollRegionHeight();
                if (rowHeight && this._listContext._scrollParameters && scrollRegionHeight > 0) {
                    var firstVisibleRow = this._listContext._getFirstVisibleRow();
                    var visibleRowCount = this._listContext._getVisibleRowCount(scrollRegionHeight);
                    var rowCountThreshold = this._listContext._RowCountThresholdFraction * visibleRowCount;
                    var renderedRowsCount = this.getRenderedRowCount();
                    if (renderedRowsCount > rowCountThreshold) {
                        var remainingAmount = Math.round(rowCountThreshold * .5);
                        // Always keep at least two viewport sizes
                        if (remainingAmount < visibleRowCount * 2) {
                            remainingAmount = visibleRowCount * 2;
                        }
                        var remainingAmountAbove = Math.floor((remainingAmount - visibleRowCount) / 2);
                        var remainingAmountBelow = Math.ceil((remainingAmount - visibleRowCount) / 2);
                        var remainingStart = firstVisibleRow - remainingAmountAbove;
                        var remainingEnd = firstVisibleRow + visibleRowCount + remainingAmountBelow - 1;
                        if (remainingEnd >= renderedRowsCount) {
                            remainingStart -= (remainingEnd - renderedRowsCount + 1);
                            remainingEnd = (renderedRowsCount - 1);
                        }

                        if (remainingStart < 0) {
                            remainingEnd -= remainingStart;
                            remainingStart = 0;
                        }

                        var removeSegmentOfRenderedRows = function (start, end) {
                            for (var i = start; i < end; ++i) {
                                var currentElement = that._getRenderedRowElementByIndex(i);
                                if (currentElement && currentElement.parentNode) {
                                    currentElement.parentNode.removeChild(currentElement);
                                }
                                var internalRow = that._getRenderedRowByIndex(i);
                                if (internalRow) {
                                    var internalRowId = internalRow.id;
                                    if (internalRowId) {
                                        delete that._rowIdToRenderedRow[internalRowId];
                                    }
                                }
                            }
                            that._rowIds.splice(start, end);
                        };
                        //Fix for jumping issue when discard policy is run for a downward scroll. It accounts for the loading data div.
                        var bufferRowCount = 0;
                        if (this._listContext.mergeBelow && this._listContext._scrollParameters.bufferBelowHeight) {
                            var bufferRowCountBelow = this._listContext._getVisibleRowCount(this._listContext._scrollParameters.bufferBelowHeight);
                            var bufferRowCountAbove = this._listContext._getVisibleRowCount(this._listContext._scrollParameters.bufferAboveHeight);

                            if (bufferRowCountBelow) {
                                bufferRowCount += bufferRowCountBelow;
                            }

                            if (bufferRowCountAbove) {
                                bufferRowCount -= bufferRowCountAbove;
                            }
                        }
                        // Remove everything outside remaining start and end
                        removeSegmentOfRenderedRows(remainingEnd + 1, renderedRowsCount);
                        removeSegmentOfRenderedRows(0, remainingStart + bufferRowCount);
                        $dyn.log.list && $dyn.log.list("List: Discard policy. Discarding " + (remainingStart + bufferRowCount) + " rows above");
                        $dyn.log.list && $dyn.log.list("List: Discard policy. Discarding " + (renderedRowsCount - remainingEnd - 1) + " rows below");

                        this._listContext.UpdateClientCacheStart(this._listContext.clientCacheStart + remainingStart, bufferRowCount);

                        return true;
                    }
                }
            }

            return false;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Returns the dom element that is the focus target in the given row with the iven cell name
        * @param {Element} rowElement - The row element to search for focus target
        * @param {string}  cellName - The cell name
        * @return {Element} The focus target DOM element
          */
        _getFocusTargetByCellName: function (rowElement, cellName) {
            var $newControlToFocus = $(rowElement).find("[data-dyn-controlname='" + cellName + "']");
            if ($newControlToFocus && $newControlToFocus.length) {
                var newfocusTargetSelector = $newControlToFocus[0].getAttribute('data-dyn-focus');
                if (newfocusTargetSelector) {
                    var $focusTarget = $newControlToFocus.find(newfocusTargetSelector);
                    return $focusTarget[0];
                }
            }
        },

        /**
        * @memberof $dyn.controls.List.Renderer
        * @typedef CellSelection
        * @summary Represents cached version of the rendered row in the dom
        * @private
        *
        * @prop {String} cell name
        * @prop {Number} selection start
        * @prop {Number} selection end
          */

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Extracts cell selection from the given row element based on currently active element
        * @param {Object} rowElement - The row element to extract cell selection from
        * @return {$dyn.controls.List.Renderer.CellSelection}  An object that contains selection information including cell name, selection start and end
          */
        _getCellSelection: function (rowElement) {
            var activeElementWithinRow = $(rowElement).find(document.activeElement);
            var focusableElement;
            var cellName;
            if (activeElementWithinRow && activeElementWithinRow.length) {
                focusableElement = document.activeElement;
                cellName = this._listContext._getParentControlName(document.activeElement);
            } else {
                // If activeElement is not part of the input row element, let's take selection information from the activeCell
                if (document.activeElement === document.body && $dyn.peek(this._activeCell)) {
                    focusableElement = this._getFocusTargetByCellName(rowElement, $dyn.peek(this._activeCell));
                    cellName = $dyn.peek(this._activeCell);
                }
            }

            if (focusableElement) {
                var selectionStart = $dyn.ui.selectionStart(focusableElement);
                var selectionEnd = $dyn.ui.selectionEnd(focusableElement);
                return { start: selectionStart, end: selectionEnd, cellName: cellName };
            }
            return undefined;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Transfers cell selection to a given rowElement based on selection object.
        * @param {Element} rowElement - The row element to transfer cell selection to
        * @param {$dyn.controls.List.Renderer.CellSelection} selection - An object that contains selection information including cell name, selection start and end
          */
        _transferCellSelection: function (rowElement, selection) {
            if (rowElement && selection && selection.cellName) {
                var focusTarget = this._getFocusTargetByCellName(rowElement, selection.cellName);
                if (focusTarget) {
                    $dyn.ui.selectionStart(focusTarget, selection.start);
                    $dyn.ui.selectionEnd(focusTarget, selection.end);
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @internal
        * @summary Transfers input focus across rows in keyboard navigation scenarios.
        * @param {Element} $targetRow - jQuery object representing the target row.
        * @param {Element} [$previouslyActiveElement] - jQuery object representing the element that was active.
        * @returns {Boolean} Success of the operation
        */
        TransferInputFocus: function ($targetRow, $previouslyActiveElement) {
            var transferFocus = true;
            var root = this._listContext.Root;
            if (root) {
                transferFocus = root.TakeFocus && root.isFormActive();
            }

            if (transferFocus) {
                var foundCellInTargetListItem = null;
                var blurEvent = null;
                var selectedCellName;

                if ($previouslyActiveElement && $previouslyActiveElement.length) {
                    var foundCell = $previouslyActiveElement.closest('.listCell');
                    if (foundCell.length) {
                        // Look up the equivalent position in the target row.
                        foundCellInTargetListItem = $targetRow.children().eq(foundCell.index());
                    }

                    var oldValue = $dyn.shell.clearBodyFocusCorrection();

                    blurEvent = jQuery.Event("blur");
                    $previouslyActiveElement.trigger(blurEvent);

        			$dyn.shell.bodyFocusCorrection = oldValue;
        		} else if ($dyn.peek(this._activeCell)) {
        			selectedCellName = $dyn.peek(this._activeCell);
        			if (selectedCellName === 'checkMarkTarget') {
        			    var $controlToFocus = $targetRow.find('.checkMarkTarget');
        				if ($controlToFocus && $controlToFocus.length) {
        				    $controlToFocus.focus();
        				}
        			}
        			else {
        			    if ($targetRow[0] === this._listContext._headerRowElement) {
        			        foundCellInTargetListItem = $targetRow.find('[data-dyn-columnname="' + selectedCellName + '"]');
        			    } else {
        			        foundCellInTargetListItem = $targetRow.find('[data-dyn-controlname="' + selectedCellName + '"]');
                        }
        			}
        		} else if (this._listContext.Root && this._listContext.Root.ActiveControl()) {
        			selectedCellName = this._listContext.Root.ActiveControl();
        			if ($targetRow[0] === this._listContext._headerRowElement) {
        			    foundCellInTargetListItem = $targetRow.find('[data-dyn-columnname="' + selectedCellName + '"]');
        			} else {
        			    foundCellInTargetListItem = $targetRow.find('[data-dyn-controlname="' + selectedCellName + '"]');
        			}
        		}

                if (foundCellInTargetListItem) {
                    var $focusTarget = null;
                    // First attempt: Find the first instance of data-dyn-focus and use that to find the focusable element. This may fail
                    // if the content contained isn't a control.
                    var $containerOfFocusableTarget = foundCellInTargetListItem.find('[data-dyn-focus]').addBack().first();
                    if ($containerOfFocusableTarget.length) {
                        var focusTargetSelector = $containerOfFocusableTarget.attr('data-dyn-focus');
                        if (focusTargetSelector) {
                            $focusTarget = $containerOfFocusableTarget.find(focusTargetSelector);
                        }
                    }

                    // Second attempt: Simply find the first occurence of an input element and use that as the focus target.
                    if (!$focusTarget || !$focusTarget.length) {
                        $focusTarget = foundCellInTargetListItem.find('input').first();
                    }

                    $dyn.log.list && $dyn.log.list.trace('List: [fast] Transfering focus to: %s', selectedCellName);
                    // Find the first input element give it focus.
                    if ((!blurEvent || !blurEvent.isDefaultPrevented()) && $focusTarget && $focusTarget.focus().length) {
                        //Select only when field is readOnly and transition happened by down arrow key
                        if (!$focusTarget.prop("readonly") && this._listContext.arrowKeyDownTriggered) {
                            $focusTarget.select();
                            if ($dyn.shell.blockingDiv && $dyn.shell.blockingDiv.style.display !== "none") {
                                var form = $dyn.context($dyn.shell.getActiveForm());
                                if (form) {
                                    var activeElement = $focusTarget[0];
                                    var focusObject = { element: activeElement, selectionStart: $dyn.ui.selectionStart(activeElement), selectionEnd: $dyn.ui.selectionEnd(activeElement), formId: form.Id };
                                    $dyn.shell.updateFocusableElement(focusObject);
                                }
                            }
                            this._listContext.arrowKeyDownTriggered = false;
                        }

        			    if (root && root.updateActiveElement) {
        			        var focusedControlElement;
        			        if ($targetRow[0] === this._listContext._headerRowElement) {
        			            focusedControlElement = $focusTarget.addBack().closest('[data-dyn-role]')[0];
        			        } else {
        			            focusedControlElement = $focusTarget.addBack().closest('[data-dyn-controlname]')[0];
        			        }
        			        
        			        root.updateActiveElement(focusedControlElement);
        				}
        			}
        		}
        	}

            return transferFocus;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @internal
        * @summary Sets active cell value
        * @param {String} activeCell - Active cell name
        */
        setActiveCell: function (activeCell) {
            this._activeCell = activeCell;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @internal
        * @summary Sets user busy flag to True. Sets a timeout on expiration of which the user busy flag becomes False.
        * The timeout value is dependent on List having pending edits.
        */
        setUserActionBusy: function () {
            var timerValue = $dyn.context(this._listContext.AllowEdit) && this._listContext.hasPendingEdits() && $dyn.peek(this._listContext._fastEdit) ? 4000 : 400;

            if (this._userActionBusyTimer) {
                this._userActionBusyTimer.cancel();
                this._userActionBusyTimer = undefined;
                $dyn.log.list && $dyn.log.list("List: [busy] User still active");
            } else {
                $dyn.log.list && $dyn.log.list("List: [busy] User active");
            }

            this._userActionBusy(true);
            var self = this;
            this._userActionBusyTimer = $dyn.async(function () {
                self._userActionBusy(false);
                $dyn.log.list && $dyn.log.list("List: [busy] User not active");
            }, timerValue);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Updates selected row by detaching active row element and attaching inactve row element or vice versa depending on the grid state
        * @param {Boolean} [renderNeeded] - The flag that indicates that full rendering process is going an active row as well
        */
        _updateSelectedRow: function (renderNeeded) {
            $dyn.log.list && $dyn.log.list("List: [Renderer] Entering UpdateSelectedRow");
            var userBusy = $dyn.peek(this._userActionBusy);
            if (userBusy) {
                $dyn.log.list && $dyn.log.list("List: [Renderer] User is busy");
            }
            else {
                $dyn.log.list && $dyn.log.list("List: [Renderer] User is not busy");
            }
            var syncEngineState = $dyn.peek(this._syncEngineState);
            if (syncEngineState === $dyn.util.SyncEngineState.active) {
                $dyn.log.list && $dyn.log.list("List: [Renderer] Sync Engine is active");
            }
            else {
                $dyn.log.list && $dyn.log.list("List: [Renderer] Sync Engine is inactive");
            }
            $dyn.log.list && $dyn.log.list("List: [Renderer] Selected Row id is %s", this._selectedRowId);

            var userSelectedRowId = $dyn.peek(this._dataProvider.InternalSelectedRowId);
            var dataSelectedRowId = $dyn.peek(this._dataProvider.DataSelectedRowId);
            if (this._activeRowInTheDOM()) {
                // Let's check if active row needs to be detached
                if (syncEngineState === $dyn.util.SyncEngineState.active || userSelectedRowId !== dataSelectedRowId) {
                    $dyn.log.list && $dyn.log.list("List: [Renderer] Detaching active row");
                    this._detachActiveRow();
                } else if (!renderNeeded) {
                    if (this._selectedRowId !== $dyn.peek(this._dataProvider.DataSelectedRowId)) {
                        this._makeRowInactive(this._selectedRowId);
                        this._makeRowActive($dyn.peek(this._dataProvider.DataSelectedRowId));
                    }
                }
            } else {
                // Let's check if active row needs to attached
                if (this._activeRowInTheDomAllowed()) {
                    $dyn.log.list && $dyn.log.list("List: [Renderer] Attaching active row");
                    this._attachActiveRow();
                }
            }
            $dyn.log.list && $dyn.log.list("List: [Renderer] Leaving UpdateSelectedRow");
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Indicates whether active row is in the DOM
        * @returns {Boolean} True if active row is in the DOM
        */
        _activeRowInTheDOM: function () {
            var isInDOM = false;
            if ($dyn.peek(this._listContext.GroupBy)) {
                var listBody;
                if (this._listContext._selectedRow) {
                    var closest = $(this._listContext._selectedRow).closest(".listBody");
                    if (closest) {
                        listBody = closest[0];
                    }
                }

                isInDOM = !!(this._listContext._selectedRow && listBody === this._listBody);
            }
            else {
                // Could improve: Cache the state and update it while rendering/making row active-inactive
                isInDOM = !!(this._listContext._selectedRow && this._listContext._selectedRow.parentElement === this._listBody);
            }

            return isInDOM;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Detaches active row from the DOM
        */
        _detachActiveRow: function () {
            if (this._selectedRowId && $dyn.length(this._listContext.DataSource) > 0) {
                this._makeRowInactive(this._selectedRowId);
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Attaches active row to the DOM
        */
        _attachActiveRow: function () {
            if (this._selectedRowId) {
                this.$_lastActiveElement = this._makeRowInactive(this._selectedRowId);
            }
            this._makeRowActive($dyn.peek(this._dataProvider.DataSelectedRowId));
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @internal
        * @summary Sets row marking attribute and visuals to the row element
        * @param {HTMLElement} rowElement
        * @param {Boolean} marked
        */
        setRowMarking: function (rowElement, marked) {
            if (rowElement) {
                marked = marked && this._listContext._supportsMarking();
                rowElement.setAttribute('data-dyn-row-marked', marked);
                rowElement.setAttribute('aria-selected', marked);
                // Set the same attribute on the marking checkbox as well
                if (rowElement.children && rowElement.children[0]) {
                    $(".marked-record-checkbox[role='checkbox']", rowElement.children[0]).attr('aria-checked', marked);
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary An event handler that is invoked when the row with the given row id became committed (created on the back end)
        * @param {Number} internalRowId - The row id
        */
        _onRowBecameCommitted: function (internalRowId) {
            var renderedRow = this._rowIdToRenderedRow[internalRowId];
            if (renderedRow) {
                renderedRow.type = this._dataProvider.committedRowType;
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Gets row state that is an array of control states. Control state is an object provided by particular control that in general contains control value(s)
        * @param {Element} rowElement - The row element to get control state for
        * @returns {Array} Row state
        */
        _getRowState: function (rowElement) {
            var state = {};
            var i;
            var len;
            var childControl;
            var rowControl;
            var children;
            if (rowElement) {
                rowControl = $dyn.context(rowElement);
            }
            if(rowControl) {
                children = $dyn.value(rowControl.Children);
            }
            if (children) {
                for (i = 0, len = children.length; i < len; i++) {
                    childControl = children[i];
                    state = this._getStateFromChildControl(childControl, state);
                }
            }
            return state;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Gets row state that is an array of control states of Inactive Row. Control state is an object provided by particular control that in general contains control value(s)
        * @param {Element} rowElement - The inactive row element to get control state for
        * @returns {Array} Row state
        */
        _getRowStateForInactiveRow: function (rowElement) {
            var state = {};
            var i;
            var len;
            var childControl;
            var children;
            if (rowElement) {
                children = rowElement.children;
            }
            if (children) {
                for (i = 0, len = children.length; i < len; i++) {
                    childControl = $dyn.context(children[i]);
                    state = this._getStateFromChildControl(childControl, state);
                }
            }
            return state;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Gets state for a child control
        * @param {childControl} childControl - For which state is asked
		* @param {state} state - state array
        * @returns {Array} Row state
        */
        _getStateFromChildControl: function (childControl,state) {
            var controlState;
            if (childControl && childControl.GetState) {
                controlState = childControl.GetState();
            }
            if (controlState) {
                state[childControl.Name] = controlState;
            }
            return state;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Applies row state to a given row element
        * @param {Array} rowState - The row state
        * @param {Element} rowElement - Row element
        */
        _applyRowState: function (rowState, rowElement) {
            var state,
                i,
                len,
                childElement,
                childControl;

            if (rowState) {
                var children = rowElement.children;
                for (i = 0, len = children.length; i < len; i++) {
                    childElement = children[i];
                    childControl = $dyn.context(childElement);

                    var controlName = childControl && childControl.Name || childElement.getAttribute('data-dyn-controlname');
                    var controlState = rowState[controlName];

                    if (controlState) {
                        if (!childControl) {
                            childControl = this._listContext._getVirtualControl(childElement);
                            childControl = childControl && childControl.current;
                        }

                        if (childControl) {
                            childControl.ApplyState && childControl.ApplyState(controlState);
                        }
                    }
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Makes a row by given row id inactive.
        * @param {Number} rowId - The row id
        */
        _makeRowInactive: function (rowId) {
            var renderedRow = this._rowIdToRenderedRow[rowId];
            if (!renderedRow || !this._listContext._selectedRow.parentNode) {
                return;
            }

            if (this._selectedRowId !== rowId || !renderedRow.selected) {
                return;
            }

            var rowData = this._dataProvider.getDataProviderItemByInternalRowId(rowId);
            var inactiveElement, version, marked, type;
            if (rowData) {
                inactiveElement = this._createNewInactiveRowElement(rowData);
                version = rowData.version;
                type = rowData.type;
                marked = rowData.marked;
            } else {
                inactiveElement = renderedRow.element;
                version = renderedRow.version;
                type = renderedRow.type;
                marked = renderedRow.marked;
            }

            var selectedElement = this._listContext._selectedRow;
            if (inactiveElement && selectedElement) {
                var parentElement = selectedElement.parentNode;
                var activeElement = document.activeElement;
                var activeControl = $dyn.context(activeElement);
                if (activeControl && $.contains(this._listBody, activeElement)) {
                    if (activeControl.updateValue) {
                        activeControl.updateValue(true);
                    }

                    if (activeControl.focusOutCleanup) {
                        activeControl.focusOutCleanup(true);
                    }
                }

                // Transfer the values of the cells from selected row to inactive row that replaces selected row
                // Scenario: User edited the row and changed the row, row becomes inactive immideately because of sync manager activation.
                // Inactive row should have values that user entered into active row
                if (rowId === $dyn.peek(this._dataProvider.DataSelectedRowId)) {
                    var rowState = this._getRowState(selectedElement);
                    this._applyRowState(rowState, inactiveElement);
                }

                this.setRowMarking(inactiveElement, !!marked);
                var previousCellSelection = this._getCellSelection(selectedElement);
                //parentElement.replaceChild triggers focusout event in chrome which removes the hasFocussedChild class from controls.
                //However, in ie that does not happen and hence we need to trigger blur manually
                var currentActiveElement = document.activeElement;
                if($.contains(selectedElement, currentActiveElement)) {
                    $dyn.ui.selectionStart(currentActiveElement, -1);
                    $dyn.ui.selectionEnd(currentActiveElement, -1);
                    $(currentActiveElement).blur();
                }
                if (parentElement) {
                    parentElement.replaceChild(inactiveElement, selectedElement);
                    if (rowId === this._dataProvider.InternalSelectedRowId() && $dyn.peek(this._listContext.Mode) === $dyn.ui.ListMode.tabular) {
                        this._transferCellSelection(inactiveElement, previousCellSelection);
                    }
                }

                if (rowId === $dyn.peek(this._invalidRow)) {
                    this._setIsInvalidClass(inactiveElement);
                }

                if ($dyn.util.isEnhancedTabSequenceEnabled()) {
                    if (inactiveElement.children && inactiveElement.children[0]) {
                        $(".marked-record-checkbox[role='checkbox']", inactiveElement.children[0]).attr('tabindex', -1);
                    }
                }

                var newRenderedRow = {
                    element: inactiveElement,
                    id: rowId,
                    version: version,
                    selected: false,
                    marked: marked,
                    order: renderedRow.order, //the order of the row didn't change
                    type: type,
                };
                this._setInternalRowId(inactiveElement, rowId);
                this._rowIdToRenderedRow[rowId] = newRenderedRow;

                inactiveElement.setAttribute("data-dyn-row-active", false);

                //Set focus if this is the user active row
                if (rowId === this._dataProvider.InternalSelectedRowId()) {
                    this.focusCellInActiveRow();
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Makes a row by given row id active.
        * @param {Number} rowId - The row id
        */
        _makeRowActive: function (rowId) {
            var renderedRow = this._rowIdToRenderedRow[rowId];
            if (!renderedRow || !renderedRow.element || !renderedRow.element.parentNode) {
                return;
            }

            if (this._selectedRowId === rowId || renderedRow.selected) {
                return;
            }

            var rowData = this._dataProvider.getDataProviderItemByInternalRowId(rowId);
            var version, marked, type;

            if (rowData) {
                version = rowData.version;
                marked = rowData.marked;
                type = rowData.type;
            } else {
                version = renderedRow.version;
                marked = renderedRow.marked;
                type = renderedRow.type;
            }

            var selectedElement = this._listContext._selectedRow;
            var inactiveElement = renderedRow.element;

            if (inactiveElement && selectedElement) {
                var parentElement = inactiveElement.parentNode;
                var previousCellSelection = this._getCellSelection(inactiveElement);
    
                var isFastEdit = $dyn.peek(this._listContext._fastEdit);
                if (isFastEdit) {
                    //Transfers states of all the childs of inactive row to the selected row.
                    //This is required so that control can have its all old state values when rendering from cell editor to full control.
                    var rowState = this._getRowStateForInactiveRow(inactiveElement);
                    this._applyRowState(rowState, selectedElement);
                }
    
                parentElement.replaceChild(selectedElement, inactiveElement);

                if (this._listContext.UpdateActiveItemBindings && this._listContext._selectedItem && inactiveElement.dynRowItem) {
                    this._listContext._selectedItem(inactiveElement.dynRowItem);
                }

                if (rowId === this._dataProvider.InternalSelectedRowId() && $dyn.peek(this._listContext.Mode) === $dyn.ui.ListMode.tabular) {
                    this._transferCellSelection(selectedElement, previousCellSelection);
                }
    
                var newRenderedRow = {
                    // By our design selected element doesn't go into rendered rows array, instead we store there its old inactive element
                    element: inactiveElement,
                    id: rowId,
                    version: version,
                    selected: true,
                    marked: marked,
                    order: renderedRow.order, //the order of the row didn't change
                    type: type,
                };
                this._setInternalRowId(selectedElement, rowId);
                selectedElement.setAttribute('data-dyn-row-active', true);

                $(selectedElement).find("[role=gridcell]").each(function(index, element)  {
                    element.setAttribute("aria-rowindex", rowId);
                });
    
                if (rowId === $dyn.peek(this._invalidRow)) {
                    this._setIsInvalidClass(selectedElement);
                } else {
                    this._removeIsInvalidClass(selectedElement);
                }
    
                this._rowIdToRenderedRow[rowId] = newRenderedRow;
    
                this.setRowMarking(selectedElement, !!marked);
    
                if ($dyn.util.isEnhancedTabSequenceEnabled()) {
                    if (selectedElement.children && selectedElement.children[0]) {
                        $(".marked-record-checkbox[role='checkbox']", selectedElement.children[0]).attr('tabindex', 0);
                    }
                }
    
                //Set focus if this is the user active row and mark row column is not clicked
                if (rowId === this._dataProvider.InternalSelectedRowId() && this._activeCell() !== "checkMarkTarget") {
                    this.focusCellInActiveRow();
                }
    
                if (this._listContext._syncManager._clientInitiatedRowChange) {
                    this._listContext._syncManager._clientInitiatedRowChange = false;
                } else {
                    this.scrollUserSelectedRowIntoView();
                }
    
                if (this._listContext._scrollParameters) {
                    this._listContext._scrollParameters.selectionFocusTriggerMax = undefined;
                    this._listContext._scrollParameters.selectionFocusTriggerMin = undefined;
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Updates the selecting row state. The row is 'selecting' if it is currently active row in the dom (not necessary on the back end).
        * @param {Number} rowId - The row id
        * @param {Element} element - An element that user is currently selecting
        */
        _updateSelectingRow: function (rowId, element) {
            var lastSelectingRowId = this._lastSelectingRowId, selectingRowId = this._selectingRowId;

            if (selectingRowId === lastSelectingRowId && this._listContext._syncManager._leaveRecordRowIndex !== null) {
                return;
            }

            // Remove selecting attr from prev row
            if (rowId === lastSelectingRowId) {
                $dyn.log.list && $dyn.log.list.trace("List: Update selecting row (last): %s, %o", rowId, element);
                if (element && element !== this._listContext._selectedRow) {
                    element.removeAttribute(this._rowSelecting);
                    $('[data-dyn-focus]', element).each(function (index, element) {
                        var focus = element.getAttribute('data-dyn-focus');
                        $(focus, element).attr('tabindex', "-1");
                    });
                }
            }

            if (rowId === selectingRowId) {
                $dyn.log.list && $dyn.log.list.trace("List: Update selecting row: %s, %o", rowId, element);
                // Add selecting attr to requested row
                if (element && element !== this._listContext._selectedRow) {
                    element.setAttribute(this._rowSelecting, true);
                    $('[data-dyn-focus]', element).each(function (index, element) {
                        var focus = element.getAttribute('data-dyn-focus');
                        $(focus, element).attr('tabindex', "0");
                    });
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary An event handler that is invoked when marked indexes chanegd on the back end. Renderer updates DOM marked state based on the MarkedIndexes array.
        */
        _markedIndexesChanged: function () {
            var enumerator = this._dataProvider.getEnumerator();
            enumerator.reset();
            while (enumerator.moveNext()) {
                var current = enumerator.current();
                var curId = current.id;
                var renderedRow = this._rowIdToRenderedRow[curId];
                // Not every row that enumerator provides is rendered at the moment
                if (renderedRow && renderedRow.marked !== current.marked) {
                    var element = this._getRenderedRowElementByRowData(renderedRow);
                    if (element) {
                        this.setRowMarking(element, !!current.marked);
                        renderedRow.marked = current.marked;
                    }
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary An event handler that is invoked when row sync state changed. Renderer updates DOM based rowSyncState array.
        */
        _rowSyncStateChanged: function () {
            var rowSyncState = this.rowSyncState();
            var enumerator = this._dataProvider.getEnumerator();
            enumerator.reset();
            while (enumerator.moveNext()) {
                var current = enumerator.current();
                var curId = current.id;
                var rowStatus = rowSyncState[curId];
                var renderedRow = this._rowIdToRenderedRow[curId];
                // Not every row that enumerator provides is rendered at the moment
                if (renderedRow) {
                    var element = this._getRenderedRowElementByRowData(renderedRow);
                    if (element) {
                        if (rowStatus) {
                            element.classList.add(this._isLoadingClass);
                        } else {
                            element.classList.remove(this._isLoadingClass);
                        }
                        renderedRow.rowStatus = rowStatus;
                    }
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary An event handler that is invoked when invalid row changed. Renderer updates DOM state based on given invalid rowId
        * @param {Number} rowId - An invalid row id
        */
        _invalidRowChanged: function (rowId) {
            if ($dyn.peek(this._listContext._fastEdit) && rowId !== this._previousInvalidRow) {
                if (this._previousInvalidRow && rowId !== this._previousInvalidRow) {
                    var renderedRow = this._rowIdToRenderedRow[this._previousInvalidRow];
                    if (renderedRow) {
                        var element = this._getRenderedRowElementByRowData(renderedRow);
                        this._removeIsInvalidClass(element);
                    }
                }

                if (rowId) {
                    var renderedRow = this._rowIdToRenderedRow[rowId];
                    if (renderedRow) {
                        var element = this._getRenderedRowElementByRowData(renderedRow);
                        this._setIsInvalidClass(element);
                    }
                }
                this._previousInvalidRow = rowId;
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary An event handler that is invoked when MarkAllRecords is changed. This updates the dom element to show if the row is marked or unmarked.
        */
        _setAllMarkedRows: function () {
            if (this.allRecordsMarkTriggered) {
                var allRows = this._rowIdToRenderedRow;
                var rowElement;
                for (var obj in allRows) {
                    if (obj) {
                        rowElement = this._getRenderedRowElementByRowData(allRows[obj]);
                    }
                    if (rowElement && this.setRowMarking) {
                        var markAll = $dyn.value(this._markAllRecords);
                        this.setRowMarking(rowElement, $dyn.util.stringToBoolean(markAll));
                    }
                }
                this.allRecordsMarkTriggered = false;
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Adds a class to given element that indicates that an element is invalid
        * @param {Element} element - An element to add invalid class to
        */
        _setIsInvalidClass: function (element) {
            element.classList.add(this._isInvalidClass);
            element.classList.remove(this._isLoadingClass);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Removes a class from given element that indicates that an element is invalid
        * @param {Element} element - An element to remove invalid class from
        */
        _removeIsInvalidClass: function (element) {
            element.classList.remove(this._isInvalidClass);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Updates an order property of the all rendered rows
        */
        _updateRenderedRowsIndexes: function () {
            for (var i = 0; i < this._rowIds.length; ++i) {
                var renderedRow = this._getRenderedRowByIndex(i);
                if (renderedRow) {
                    renderedRow.order = i;
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Gets rendered row element by given rendered row data
        * @param {$dyn.controls.List.Renderer.RenderedRow} rowData - rendered row data
        * @returns {Element} Rendered row element
        */
        _getRenderedRowElementByRowData: function (rowData) {
            if (rowData) {
                if (rowData.selected) {
                    return this._listContext._selectedRow;
                } else {
                    return rowData.element;
                }
            }
            return undefined;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Gets rendered row element by given rendered row id
        * @param {Number} rowId - rendered row id
        * @returns {Element} Rendered row element
        */
        _getRenderedRowElementById: function (rowId) {
            var rowData = this._getRenderedRowById(rowId);
            return this._getRenderedRowElementByRowData(rowData);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Gets rendered row element by given rendered row index
        * @param {Number} index - rendered row index
        * @returns {Element} Rendered row element
        */
        _getRenderedRowElementByIndex: function (index) {
            var rowData = this._getRenderedRowByIndex(index);
            return this._getRenderedRowElementByRowData(rowData);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Sets internal row id to a given row element
        * @param {Element} element - Rendered row element
        * @param {Number} rowId - row id
        */
        _setInternalRowId: function (element, rowId) {
            element.setAttribute('data-dyn-rowid', rowId);
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Indicates whether an active row is allowed to be rendered in the DOM
        * @returns {Boolean} Rendered row element
        */
        _activeRowInTheDomAllowed: function () {
            var userBusy = $dyn.peek(this._userActionBusy);
            var syncEngineState = $dyn.peek(this._syncEngineState);
            var userSelectedRowId = $dyn.peek(this._dataProvider.InternalSelectedRowId);
            var dataSelectedRowId = $dyn.peek(this._dataProvider.DataSelectedRowId);
            return !userBusy && syncEngineState === $dyn.util.SyncEngineState.inactive && userSelectedRowId === dataSelectedRowId;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Gets internal row id by given row element
        * @param {Element} element
        * @returns {Number} Row id
        */
        getInternalRowIdByElement: function (element) {
            if (element) {
                var rowId = parseInt(element.getAttribute('data-dyn-rowid'), 10);
                return isNaN(rowId) ? undefined : rowId;
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Gets a row element that is currently selected in the DOM
        * @returns {Element} User selected element
        */
        getUserSelectedElement: function () {
            return this._getRenderedRowElementByRowData(this.getUserSelectedRenderedRow());
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Gets a row element that is currently selected from server.
        * @returns {Element} User selected element
        */
        getServerSelectedElement: function () {
            return this._getRenderedRowElementByRowData(this.getServerSelectedRenderedRow());
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @internal
        * @summary Indicates whether the dom has selected row
        * @returns {Boolean} True if the dom has selected row
        */
        isUserSelectedRowRendered: function () {
            return !!this.getUserSelectedElement();
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @internal
        * @summary Gets the rendered row object of the currently selected row in the dom
        * @returns {$dyn.controls.List.Renderer.RenderedRow} Rendered row
        */
        getUserSelectedRenderedRow: function () {
            var userSelectedId = $dyn.peek(this._dataProvider.InternalSelectedRowId);
            var renderedRow = this._rowIdToRenderedRow[userSelectedId];
            return renderedRow;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @internal
        * @summary Gets the rendered row object of the currently selected row in server
        * @returns {$dyn.controls.List.Renderer.RenderedRow} Rendered row
        */
        getServerSelectedRenderedRow: function () {
            var serverSelectedId = $dyn.peek(this._dataProvider._getDataSelectedRowId());
            var renderedRow = this._rowIdToRenderedRow[serverSelectedId];
            return renderedRow;
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @internal
        * @summary Ensures that active row has focus in the active cell.
        * @returns {Boolean} Success of the operation
        */
        focusCellInActiveRow: function () {
            var userSelectedElement = this._listContext.AllowHeaderFocus() ? this._listContext._headerRowElement : this.getUserSelectedElement();
            return this.TransferInputFocus($(userSelectedElement));
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @internal
        * @summary Ensures that user selected row is in the view
        */
        scrollUserSelectedRowIntoView: function () {
            this._listContext.scrollItemIntoView(this.getUserSelectedElement());
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Restores the focus.
        * @description Used by rendering algorithm. The focus needs to be restored if the previously focused elemht was detached (rerendered)
        * In case the grid is in the InfinteScroll mode, the active row is not being focused but the position of the row is being save to be focused when it becomes visible in the viewport
        */
        restoreSelectionFocus: function () {
            if (this._listContext.Root && this._listContext.Root.isFormActive() && $dyn.value(this._listContext.Root.TakeFocus) && $dyn.value(this._listContext.Root.ReadyState) !== $dyn.ui.ReadyState.aborted) {
                var activecontrol = $dyn.value(this._listContext.Root.ActiveControl());
                if (!activecontrol) {
                    return;
                }

                // Don't affect focus if there are no matching children, or if the only match is contained by the "place holder" row.
                var $matchingControls = $(selector().attr('data-dyn-controlname', activecontrol).find(this._listContext.element));
                if ($matchingControls.length === 0) {
                    return;
                } else if ($matchingControls.length === 1 && $matchingControls.closest(".place-holder").length !== 0) {                    
                    if ($dyn.value(this._listContext.IsNoResultsMessageDisplayed)) {
                        this._listContext.focusNoRowIndicator();
                    }
                    return;
                }

                var userSelectedElement = this.getUserSelectedElement();
                var scrollParameters = this._listContext._scrollParameters;
                if (userSelectedElement) {
                    if (scrollParameters) {
                        var selectedItemTop = userSelectedElement.offsetTop;
                        var selectedItemBottom = selectedItemTop + scrollParameters.rowHeight;
                        var selectionFocusTriggerMax = selectedItemBottom;
                        var selectionFocusTriggerMin = selectedItemTop - this._listContext.GetScrollRegionHeight();
                        // Selected row is inside scroll region completely
                        var scrollRegionScrollTop = this._listContext.GetScrollRegionTop();
                        if (scrollRegionScrollTop >= selectionFocusTriggerMin && scrollRegionScrollTop <= selectionFocusTriggerMax) {
                            this.focusCellInActiveRow();
                            scrollParameters.selectionFocusTriggerMax = undefined;
                            scrollParameters.selectionFocusTriggerMin = undefined;
                        } else {
                            var scrollRegion = this._listContext.GetScrollRegion();
                            scrollParameters.selectionFocusTriggerMax = selectionFocusTriggerMax;
                            scrollParameters.selectionFocusTriggerMin = selectionFocusTriggerMin;
                            //When we know we want focus in header then give the focus to header else give to scroll region
                            if (this._listContext.AllowHeaderFocus())
                            {
                                this.focusCellInActiveRow();
                            }
                            else if (document.activeElement !== scrollRegion) {
                                // Let's focus grid's scrollRegion in this case.
                                $dyn.log.focus && $dyn.log.focus("Grid->Focus the scroll region after rendering infinite scroll grid");
                                scrollRegion.focus();
                            }
                        }
                    } else {
                        this.focusCellInActiveRow();
                    }
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Renders full grid body by processing the DataSource and updating DOM
        * @param {Element} listBody - List body element
        * @param {Function} postRender - Post render callback
        * @param {Boolean} fullRender - Indicates whether rendering should rerender all rows without an attempt to reuse not changed rows.
        */
        renderBody: function (listBody, postRender, fullRender) {
            var that = this;
            // We have an exception for Loaded Data rendering
            var dataLoadedRenderPending = $dyn.peek(this._listContext._dataLoadedRenderPending);
            if (dataLoadedRenderPending) {
                this._forceRender = true;
            }
            var renderingMergeMode = dataLoadedRenderPending && this._listContext._infiniteScrollMode() === $dyn.util.InfiniteScrollMode.fast;
            this._renderAll(listBody, postRender, fullRender && !$dyn.peek(this._userActionBusy)/*No full render if user is busy*/, renderingMergeMode);
            this._lastRenderMode = dataLoadedRenderPending ? this._renderModeMerge : this._renderModeReplace;
            if (fullRender && $dyn.peek(this._userActionBusy)) {
                this._fullRenderPostponed = true;
            }
            if ($dyn.peek(this._userActionBusy) && !$dyn.peek(that._userActionBusyRenderPending)) {
                that._userActionBusyRenderPending(true);
                this._disposables.push($dyn.observe(this._userActionBusy, function (userBusy) {
                    if (!userBusy) {
                        if (that._fullRenderPostponed) {
                            fullRender = true;
                            that._fullRenderPostponed = false;
                        }
                        var renderingMergeMode = that._lastRenderMode === that._renderModeMerge && that._listContext._infiniteScrollMode() === $dyn.util.InfiniteScrollMode.fast;
                        that._renderAll(listBody, postRender, fullRender, renderingMergeMode);
                        that._userActionBusyRenderPending(false);
                        return false;
                    }
                }));
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Renders full grid body by processing the DataSource and updating DOM
        * @param {Element} listBody - List body element
        * @param {Function} postRender - Post render callback
        * @param {Boolean} fullRender - Indicates whether rendering should rerender all rows without an attempt to reuse not changed rows.
        * @param {Boolean} mergeMode - Merge mode indicates whether rendering algorithm should preserve all previously rendered rows
        * that are outside of current page. There are two modes: replace and merge.
        * @returns {Object} Object describing the changeSkipped value
        */
        _renderAll: function (listBody, postRender, fullRender, mergeMode) {
            /// RENDERING LOGIC:
            /// - Check the grid state that may influence the InfiniteScroll ability and partial rendering (GroupBy, ExtendedStyle)
            /// - Calculate rows that are candidates to be reused, based on new page position
            /// - Go through main rendering loop. Reuse rows that can be reused, move rows that can be reused but in different order, create new rows
            /// - Within rendering loop handle active row specially as active row's element is always present and updated through binding
            /// - Update client cache start index
            /// - After rendering loop Remove rows that are needed to be removed
            /// - Render newly created rows, handle placeholder row if needed
            /// - Merge rendered rows cache with just rendered rows
            /// - Handle group by
            /// - Readjust infinite scroll related state
            /// - Run discard policy
            /// - Make sure that viewport is full
            /// - Update aria attributes on rendered rows,
            /// - Post render
            /// - Restore focus

            var startTime = performance.now();
            var that = this;
            this._renderForEachTask && this._renderForEachTask.start();
            var collection = $dyn.value(this._dataProvider.DataSource);            

            // Indicates whether rendering process skipped some change, like 'create', 'update', 'move', 'remove'
            var changeSkipped = false;
            if (this._listContext._inactiveItemElement && collection) {
                this._isRendering = true;
                this._listContext._skipOffsetParentCheck = true;
                var isFastEdit = $dyn.peek(this._listContext._fastEdit);
                // Don't take a dependency on selectedIndex as it is observed separately
                var selectedIndex = $dyn.peek(this._listContext.SelectedIndex);
                var markedEnabled = this._listContext._supportsMarking();
                var arrayLength = collection.length;
                var restrictedRendering = this.restrictedRenderingMode();

                var groupBy = $dyn.value(this._listContext.GroupBy);

                // GroupBy feature doesn't support infinite scroll and partial rendering
                if (groupBy) {
                    $dyn.setValue(this._listContext, 'InfiniteScroll', false);
                    fullRender = true;
                }

                if ((groupBy || this._renderedGroupBy) && !restrictedRendering) {
                    $dyn.util.listEmpty(listBody, false);
                    fullRender = true;
                }

                // The grid with applied extended style doesn't support infinite scroll
                // as rows in the grid may be layed out horizontally ad break some scrolling logic assumptions
                if (this._listContext.ExtendedStyle) {
                    $dyn.setValue(this._listContext, 'InfiniteScroll', false);
                }

                $dyn.log.list && $dyn.log.list("List: Starting rendering. Server state: CacheSize: %i, FetchedDataStartPosition: %i, DataAbove: %s, DataBelow: %s, PageSize: %i. Client state: CacheStart: %i, RowCount: %i, FullRender: %s, mergeMode: %s",
                    $dyn.value(this._listContext.CacheSize), $dyn.value(this._listContext.FetchedDataStartPosition), $dyn.value(this._listContext.DataAbove), $dyn.value(this._listContext.DataBelow), $dyn.value(this._listContext.DataSource).length, this._listContext.clientCacheStart, this.getRenderedRowCount(), fullRender, mergeMode);

                if (fullRender) {
                    mergeMode = false;
                }

                if (this._listContext._deferred) {
                    var wasUpdatingSet = false;
                    if (!$dyn.peek($dyn.ui.updating)) {
                        $dyn.ui.updating(true);
                        wasUpdatingSet = true;
                    }

                    // Only defer on initial render. Subsequent renders for create, etc., should be performed immediately
                    // so that there are minimal delays, focus is maintained and all binding handlers are operating against
                    // the final instance of the row.
                    this._listContext._deferred = false;
                }

                var layoutBlockSet = $dyn.ui.layout();
                if (!layoutBlockSet) {
                    /// Put the handler into an updating block so that newly created children in the grid with layout bindings will be batched so that only the
                    /// topmost element gets laid out at the end.
                    $dyn.ui.layout(true);
                }

                if (this._recalculateRowHeightWithinLayoutBlock) {
                    // After layout is run it can change the rowHeight, because layout classes have now been applied
                    // Will probably only be different between the first and second render
                    if (this._layoutSubscriptionId) {
                        $dyn.ui.layout.valueChanged.unsubscribe(this._layoutSubscriptionId);
                        this._layoutSubscriptionId = null;
                    }

                    this._layoutSubscriptionId = $dyn.ui.layout.valueChanged.subscribe(function (layoutRunning) {
                        if (!$dyn.value(layoutRunning)) {
                            var currentRowHeight = this._rowHeight;
                            // If it is the same then we assume that value is correct and we no longer need to recalculate it
                            // Otherwise layout may still change the value and thus we would want to recalculate
                            if (currentRowHeight === this.getRowHeight(this._listBody, true)) {
                                this._recalculateRowHeightWithinLayoutBlock = false;
                            }

                            $dyn.ui.layout.valueChanged.unsubscribe(this._layoutSubscriptionId);
                            this._layoutSubscriptionId = null;
                        }
                    }, this);
                }

                if (fullRender) {
                    this._listContext._updateDefaultActionEnabled();
                }

                var groupValue,
                    curGroupValue,
                    groupList,
                    groupByContext,
                    inactiveGroupByElement,
                    groupTemplate,
                    groupElement;

                if (groupBy) {
                    inactiveGroupByElement = $(this._listContext._inactiveItemElement).find('[data-dyn-controlname="' + groupBy + '"]')[0];
                    groupTemplate = $dyn.templates.GroupTemplate;
                }

                var instance;
                var change;
                var currentFragment = null;
                var currentInsertAfterElement = null;
                var documentFragments = [];
                var insertAfterElements = [];
                var currentChangeBucket = null;
                var changes = [];
                var insertAfterIds = [];
                var currentInsertAfterId = null;

                var mergeAbove = false;
                var mergeBelow = false;

                // Let's identify direction of merge if we decided to preserve old rows
                // In the case of 'merge below' we have to find the element and row id of the row that we insert after
                if (mergeMode) {
                    var mergeDirection = this._renderingMergeDirection();
                    if (mergeDirection.mergeAbove === mergeDirection.mergeBelow) {
                        $dyn.logError("Direction of loaded data is not defined. Skipping the rendering.");
                        this._postRenderAll(false, restrictedRendering, wasUpdatingSet, layoutBlockSet, postRender);
                        this._cleanupRenderAll(startTime);
                        return { changeSkipped: changeSkipped };
                    }
                    currentInsertAfterId = mergeDirection.insertAfterId;
                    currentInsertAfterElement = mergeDirection.insertAfterElement;
                    mergeAbove = mergeDirection.mergeAbove;
                    mergeBelow = mergeDirection.mergeBelow;
                }

                this._lastSelectingRowId = this._selectingRowId;
                this._selectingRowId = $dyn.peek(this._dataProvider.InternalSelectedRowId);

                var createAllowed = function (item, existingData) {
                    if (item.type === that._dataProvider.committedRowType) {
                        // Note: Doesn't cover the change of active row when rendering is restricted
                        return !restrictedRendering || that._forceRender;
                    } else if (!existingData) {
                        return true;
                    }
                    else {
                        return false;
                    }

                };

                var moveAllowed = function (item) {
                    return !restrictedRendering || that._forceRender;
                };

                var updateAllowed = function (item) {
                    var updateAllowed = moveAllowed(item);
                    // Do not allow 'update' change when the row has pending edit that wasn't yet committed to server
                    // Currently 'update' change handling rerenders the whole row
                    if (updateAllowed && (that._rowHasPendingEdit(item.id) || $dyn.peek(that._invalidRow) === item.id)) {
                        return false;
                    }
                    return updateAllowed;
                };

                var removeAllowed = function (item) {
                    if (item.type === that._dataProvider.committedRowType) {
                        return moveAllowed(item);
                    }
                    return true;
                };

                var produceCreateChange = function (item, element, options) {
                    createNewFragmentIfNeeded();
                    that._setInternalRowId(element, item.id);

                    createNewChangeBucketIfNeeded();
                    var refreshedRow = {
                        element: element,
                        id: item.id,
                        version: item.version,
                        selected: false,
                        marked: markedEnabled && item.marked,
                        type: item.type,
                        rowStatus: item.rowStatus
                    };
                    $.extend(refreshedRow, options);
                    change = {
                        type: 'create',
                        renderedRow: refreshedRow,
                        id: item.id,
                    };
                    if (refreshedRow.selectedElement) {
                        that.setRowMarking(refreshedRow.selectedElement, !!item.marked);
                    }
                    that.setRowMarking(element, !!item.marked);

                    if (item.rowStatus) {
                        element.classList.add(that._isLoadingClass);
                    } else {
                        element.classList.remove(that._isLoadingClass);
                    }

                    if ($dyn.peek(that._invalidRow) === item.id) {
                        that._setIsInvalidClass(element);
                    }

                    return change;
                };

                var produceReuseChange = function (item, element, existingRowData) {
                    // As we are reusing an element we have to 'close' the document fragment
                    var elementToReuse = element || existingRowData.element;
                    currentFragment = null;
                    currentChangeBucket = null;
                    currentInsertAfterElement = elementToReuse;
                    currentInsertAfterId = item.id;
                    lastReusedOrder = elementsOrder;

                    if (existingRowData && existingRowData.marked !== item.marked) {
                        elementToReuse && that.setRowMarking(elementToReuse, !!item.marked);
                    }
                    if (existingRowData && existingRowData.rowStatus !== item.rowStatus) {
                        if (elementToReuse) {
                            if (item.rowStatus) {
                                elementToReuse.classList.add(that._isLoadingClass);
                            } else {
                                elementToReuse.classList.remove(that._isLoadingClass);
                            }
                        }
                    }
                    reusedRowIds[existingRowData.id] = true;

                };

                var produceMoveChange = function (item, element, existingRowData, options) {
                    // In this case an element has incorrect order relatively to last reused element
                    // We will add it to document fragment
                    createNewFragmentIfNeeded();
                    createNewChangeBucketIfNeeded();
                    var refreshedRow = {
                        element: element,
                        id: item.id,
                        version: item.version,
                        selected: false,
                        marked: markedEnabled && item.marked,
                        type: item.type,
                        rowStatus: item.rowStatus,
                    };
                    $.extend(refreshedRow, options);
                    change = {
                        type: 'move',
                        renderedRow: refreshedRow,
                        id: item.id,
                    };
                    if (existingRowData && existingRowData.marked !== item.marked) {
                        elementToReuse && that.setRowMarking(elementToReuse, !!item.marked);
                    }

                    if (existingRowData && existingRowData.rowStatus !== item.rowStatus) {
                        if (elementToReuse) {
                            if (item.rowStatus) {
                                elementToReuse.classList.add(that._isLoadingClass);
                            } else {
                                elementToReuse.classList.remove(that._isLoadingClass);
                            }
                        }
                    }

                    movedRowIds[item.id] = true;

                    return change;
                };

                var createNewFragmentIfNeeded = function () {
                    if (!currentFragment) {
                        currentFragment = document.createDocumentFragment();
                        documentFragments.push(currentFragment);
                        insertAfterElements.push(currentInsertAfterElement);
                    }
                };

                var createNewChangeBucketIfNeeded = function () {
                    if (!currentChangeBucket) {
                        currentChangeBucket = [];
                        changes.push(currentChangeBucket);
                        insertAfterIds.push(currentInsertAfterId);
                    }
                };

                var rowSyncState = this.rowSyncState();

                var reusedRowIds = {};
                var movedRowIds = {};
                var updatedRowIds = {};
                var renderedSelectedRow = false;
                var lastReusedOrder = -1;
                var renderSelectedRow = this._activeRowInTheDomAllowed();
                var _createdCount = 0, _reusedCount = 0, _movedCount = 0, _updatedCount = 0, _removedCount = 0;
                var arrayIndex = 0;
                if (isFastEdit) {
                    var hasCollection = false;
                    var position = 0;
                    if (collection.length > 0) {
                        hasCollection = true;
                    }

                    if (this._listContext._syncManager.isQueueEmpty()) {
                        this._dataProvider._firstRowDeleted = false;
                        this._firstRowDeleted = false;
                        this._deletedPendingRows = null;
                        this._dataProvider.lastDeletedActiveRowId = -1;
                    }
                    else if (this._deletedPendingRows) {
                        var deletedPendingArraySize = $dyn.util.calculateAssociativeArraySize(this._deletedPendingRows);
                        if ((deletedPendingArraySize + collection.length) !== this.getRenderedRowCount() && this._dataProvider.lastDeletedActiveRowId >= 0) {
                            var rowElement = this.getUserSelectedElement();
                            //First row by default
                            var selectedRowIndex = 0;

                            if (rowElement) {
                                var currentRowId = this.getInternalRowIdByElement(rowElement);
                                var rowIdByIndex = this._rowIds[selectedRowIndex];
                                if (currentRowId !== rowIdByIndex) {
                                    //First attempt: Try get the index based off of server if the deletion did not happened at server
                                    selectedRowIndex = this.getActivePageRelatedIndex(currentRowId);
                                }
                                //Second attempt: If deletion in server happened already then we have to get the selected row index from data provider
                                if (!selectedRowIndex) {
                                    selectedRowIndex = $dyn.value(this._dataProvider.SelectedIndex);
                                }
                            }
                            var onlyCommittedRowDeleted = false;
                            if (collection.length == 0) {
                                onlyCommittedRowDeleted = true;
                            }
                            this._dataProvider.updatePivotTablesAfterDelete(this._dataProvider.lastDeletedActiveRowId, selectedRowIndex, onlyCommittedRowDeleted);
                            this._dataProvider.lastDeletedActiveRowId = -1;
                        }
                    }

                }

                var enumerator = this._dataProvider.getEnumerator();
                enumerator.reset();
                var item;

                /**
                * @function
                * @memberof $dyn.controls.List.Renderer#
                * @private
                * @summary Updates fragment with the rows to be rendered
                * @param {item} item - row item to be added to fragment
                * @param {change} change - change object having the change from old instance
                * @param {instance} instance - row element
                * @param {currentFragment} currentFragment - where the items are added
                * @param {currentChangeBucket} currentChangeBucket - Holding all the changes
                * @param {arrayIndex} arrayIndex - counter for array
                */
                var updateFragment = function updateFragment(item, change, instance, currentFragment, currentChangeBucket, arrayIndex) {
                    if (currentFragment && instance) {
                        currentFragment.appendChild(instance);
                    }

                    if (item.id === that._lastSelectingRowId || item.id === that._selectingRowId) {
                        that._updateSelectingRow(item.id, instance);
                    }

                    if (currentChangeBucket && change) {
                        currentChangeBucket.push(change);
                    }

                    that._itemTask && that._itemTask.stop(arrayIndex);
                    arrayIndex++;
                };

                // The rendering loop: iterates over rows and produces changes that need to be made to update DOM and client row cache
                // Produces two type of changes: 1) Dom related; 2) Client cache related
                // documentFragments and insertAfterElements are dom related changes
                // 'changes' and 'insertAfterIds' are client row cache related
                // reusedRowIds, movedRowIds, updateRowIds - helpful objects to decide what rows should be deleted
                // We distinguish the next types of changes: 'create', 'update' (hadnled as create for now), 'move', 'delete' (derived from other changes)
                while (enumerator.moveNext()) {
                    this._itemTask && this._itemTask.start(arrayIndex);
                    if (!this._firstRowDeleted) {
                        item = enumerator.current();
                    }
                    else {
                        item = enumerator.current(true, position);
                        position++;
                    }
                    if (item) {
                        instance = null;
                        var itemId = item.id;
                        change = null;
                        var renderedElement;
                        var existingRowData = this._rowIdToRenderedRow[itemId];
                        var rowStatus = rowSyncState[itemId];
                        item.rowStatus = rowStatus;
                        var deletedItem = null;
                        if (!(renderSelectedRow && item.selected)) {
                            if (itemId) {
                                var version = item.version;
                                if (fullRender || !existingRowData || existingRowData.selected) {
                                    if (createAllowed(item, existingRowData)) {
                                        if (this._deletedPendingRows && item.type !== this._dataProvider.committedRowType) {
                                            deletedItem = this._deletedPendingRows[item.id];
                                        }
                                        if (deletedItem) {
                                            instance = deletedItem.element;
                                        }
                                        else {
                                            instance = that._createNewInactiveRowElement(item);
                                        }

                                        change = produceCreateChange(item, instance);
                                        _createdCount++;
                                    } else {
                                        if (existingRowData) {
                                            produceReuseChange(item, null, existingRowData);
                                            _reusedCount++;
                                        }
                                        changeSkipped = true;
                                    }
                                } else {
                                    var elementToReuse = existingRowData.element;
                                    var elementsOrder = existingRowData.order;
                                    if (existingRowData.version !== version) {
                                        if (updateAllowed(item)) {
                                            if (this._deletedPendingRows && item.type !== this._dataProvider.committedRowType) {
                                                deletedItem = this._deletedPendingRows[item.id];
                                            }
                                            if (deletedItem) {
                                                instance = deletedItem.element;
                                            }
                                            else {
                                                instance = that._createNewInactiveRowElement(item);
                                            }
                                            change = produceCreateChange(item, instance);
                                            updatedRowIds[itemId] = existingRowData;
                                            _updatedCount++;
                                        } else {
                                            produceReuseChange(item, elementToReuse, existingRowData);
                                            _reusedCount++;
                                            changeSkipped = true;
                                        }
                                    } else {
                                        // We are reusing an element in correct order (There were no other element reused after this)
                                        if (elementsOrder > lastReusedOrder) {
                                            produceReuseChange(item, elementToReuse, existingRowData);
                                            _reusedCount++;
                                        } else {
                                            if (moveAllowed(item)) {
                                                change = produceMoveChange(item, elementToReuse, existingRowData);
                                                _movedCount++;
                                            } else {
                                                produceReuseChange(item, elementToReuse, existingRowData);
                                                _reusedCount++;
                                                changeSkipped = true;
                                            }
                                        }
                                        instance = elementToReuse;
                                    }
                                }
                            } else {
                                $dyn.logError("List - [Rendering] Rendering loop. ItemId is not valid. Skipping the item");
                            }
                        } else {
                            instance = this._listContext._selectedRow;
                            instance.setAttribute("data-dyn-row-active", true);
                            var version = item.version;
                            if (itemId) {
                                var existingRowData = this._rowIdToRenderedRow[itemId];
                                if (!existingRowData || !existingRowData.selected) {
                                    if (createAllowed(item)) {
                                        change = produceCreateChange(item, that._createNewInactiveRowElement(item), { selected: true, selectedElement: instance });
                                        _createdCount++;
                                    } else {
                                        if (existingRowData) {
                                            produceReuseChange(item, instance, existingRowData);
                                            _reusedCount++;
                                        }
                                        changeSkipped = true;
                                    }
                                } else {
                                    var elementsOrder = existingRowData.order;
                                    // We are reusing an element in correct order (There were no other element reused after this)
                                    if (
                                        !fullRender &&
                                        elementsOrder > lastReusedOrder) {
                                        produceReuseChange(item, instance, existingRowData);
                                        _reusedCount++;
                                    } else {
                                        if (moveAllowed(item)) {
                                            change = produceMoveChange(item, that._createNewInactiveRowElement(item), existingRowData, { selected: true });
                                            _movedCount++;
                                        } else {
                                            produceReuseChange(item, instance, existingRowData);
                                            _reusedCount++;
                                            changeSkipped = true;
                                        }
                                    }
                                }

                                this._setInternalRowId(instance, itemId);
                                if ($dyn.util.isEnhancedTabSequenceEnabled()) {
                                    if (instance.children && instance.children[0]) {
                                        $(".marked-record-checkbox[role='checkbox']", instance.children[0]).attr('tabindex', 0);
                                    }
                                }
                                renderedSelectedRow = true;
                            } else {
                                $dyn.logError("List - [Rendering] Rendering loop. ItemId is not valid. Skipping the item");
                            }
                        }

                        // Handle grouping of items
                        if (groupBy && !restrictedRendering) {
                            // We have to create new inactive item element and we can't use selected one
                            // as selected item can receive values later and we need formattedValue right now
                            if (!(renderSelectedRow && itemId === $dyn.peek(this._dataProvider.DataSelectedRowId))) {
                                this._listContext._inactiveItem(item);
                            }
                            groupByContext = $dyn.context(inactiveGroupByElement);
                            if (groupByContext) {
                                curGroupValue = $dyn.value(groupByContext.formattedValue);
                                if (groupValue !== curGroupValue) {
                                    if (groupList) {
                                        groupList.appendChild(currentFragment);
                                        groupList = null;
                                    }
                                    if (!groupList) {
                                        groupElement = groupTemplate.cloneNode(true);
                                        groupElement.getElementsByClassName('listGroupTitle')[0].innerText = curGroupValue;
                                        groupList = groupElement.getElementsByClassName('listGroupContent')[0];
                                        listBody.appendChild(groupElement);
                                    }
                                    groupValue = curGroupValue;
                                }
                            }
                        }

                        updateFragment(item, change, instance, currentFragment, currentChangeBucket, arrayIndex);
                    }
                }

                this._lastSelectingRowId = this._selectingRowId;

                /// POST RENDERING LOOP LOGIC:
                //  1) Update client cache start
                //  2) Remove logic
                //  3) Placeholder logic
                //  4) Inserting new document fragments
                //  5) Updating client row cache: rowIds, rowIdToRenderedRow
                //  6) Group by logic
                //  7) Other post render stuff

                //  1) Update client cache start
                this._updateClientCacheStart(mergeMode, mergeAbove);

                //  2) Remove logic
                var removeElement = function (item) {
                    if (item) {
                        var elementToDelete = item.element;
                        if (elementToDelete && elementToDelete.parentNode) {
                            elementToDelete.parentNode.removeChild(elementToDelete);
                            _removedCount++;
                        }
                        if (!renderedSelectedRow && item.selected && that._listContext._selectedRow.parentNode) {
                            that._listContext._selectedRow.parentNode.removeChild(that._listContext._selectedRow);
                            _removedCount++;
                        }
                        var internalRowId = item.id;
                        if (internalRowId) {
                            delete that._rowIdToRenderedRow[internalRowId];
                        }
                    }
                };

                var removedIds = {};
                if (!mergeMode) {
                    for (var i = 0; i < this.getRenderedRowCount() ; ++i) {
                        var item = this._getRenderedRowByIndex(i);
                        if (item && removeAllowed(item)) {
                            var itemId = item && item.id;
                            if (itemId) {
                                if (!reusedRowIds[itemId] && !movedRowIds[itemId]) {
                                    removeElement(item);
                                    removedIds[itemId] = true;
                                }
                                if (movedRowIds[itemId]) {
                                    removedIds[itemId] = true;
                                }
                            } else {
                                removeElement(item);
                            }
                        } else {
                            changeSkipped = true;
                        }

                    }
                } else {
                    // All updated rows need to be removed as they will be reinserted again by assumption of how 'update' is handled
                    var updatedRowIdsKeys = Object.keys(updatedRowIds);
                    for (var i = 0, len = updatedRowIdsKeys.length; i < len; ++i) {
                        var item = updatedRowIds[updatedRowIdsKeys[i]];
                        removeElement(item);
                        removedIds[item.id] = true;
                    }
                }

                // Clean the rowIds array
                for (var i = 0; i < this._rowIds.length;) {
                    if (removedIds[this._rowIds[i]]) {
                        this._rowIds.splice(i, 1);
                    } else {
                        ++i;
                    }
                }
                /*Remove logic*/

                $dyn.log.list && $dyn.log.list("List: Rendering: Created count: %i, Reused count: %i, Moved count: %i, Removed count: %i", _createdCount, _reusedCount, _movedCount, _removedCount);

                //  3) Placeholder logic
                // If there's no data to be rendered, conditionally render out a placeholder row.
                if (arrayLength === 0) {
                    createNewFragmentIfNeeded();
                    this._listContext._selectedRow.classList.add("place-holder");
                    currentFragment.appendChild(this._listContext._selectedRow);
                    this._rowIdToRenderedRow[this._placeholderRowId] = {
                        element: null,
                        selected: true,
                        marked: false,
                        order: 0,
                        id: this._placeholderRowId,
                    };

                    this._rowIds.push(this._placeholderRowId);
                    $dyn.log.list && $dyn.log.list("List: Rendering placeholder row");
                } else {
                    this._listContext._selectedRow.classList.remove("place-holder");
                }

                //  4) Inserting new document fragments
                if (groupList && currentFragment) {
                    groupList.appendChild(currentFragment);
                    groupList = null;
                } else {
                    for (var i = 0, len = documentFragments.length; i < len; ++i) {
                        var elementToInsertAfter = insertAfterElements[i];
                        if (elementToInsertAfter && elementToInsertAfter.parentNode && elementToInsertAfter.parentNode === listBody) {
                            $dyn.log.list && $dyn.log.list("List: Rendering insert before row.%o", elementToInsertAfter.nextSibling);
                            listBody.insertBefore(documentFragments[i], elementToInsertAfter.nextSibling);
                        } else {
                            if (i === 0 && (elementToInsertAfter === null || !elementToInsertAfter.parentNode)) {
                                $dyn.log.list && $dyn.log.list("List: Rendering insert at the begin.%o", listBody.firstChild);
                                if (listBody.firstChild) {
                                    listBody.insertBefore(documentFragments[i], listBody.firstChild);
                                }
                                else {
                                    listBody.appendChild(documentFragments[i]);
                                }
                            } else {
                                $dyn.logError('List: Insertion point unknown for dom fragment');
                            }
                        }
                    }
                }

                //  5) Updating client row cache: rowIds, rowIdToRenderedRow
                var updateRenderedRowsCache = function (index, changeBucket) {
                    var rowIds = [];
                    for (var j = 0; j < changeBucket.length; ++j) {
                        var change = changeBucket[j];
                        if (change.type === 'create' || change.type === 'move') {
                            rowIds.push(change.id);
                            that._rowIdToRenderedRow[change.id] = change.renderedRow;
                        }
                    }
                    Array.prototype.splice.apply(that._rowIds, [index, 0].concat(rowIds));
                };

                var findIndexOfRowById = function (id) {
                    for (var j = 0; j < that._rowIds.length; ++j) {
                        if (that._rowIds[j] === id) {
                            return j;
                        }
                    }
                };

                // Reset fullRender flag
                fullRender = false;
                for (var i = 0, len = changes.length; i < len; ++i) {
                    var changeBucket = changes[i];
                    var insertAfterId = insertAfterIds[i];
                    if (!insertAfterId) {
                        updateRenderedRowsCache(0, changeBucket);
                    } else {
                        // Could improve: Don't do linear search on every bucket
                        updateRenderedRowsCache(findIndexOfRowById(insertAfterId) + 1, changeBucket);
                    }
                }

                //  6) Group by logic
                if (groupBy) {
                    // Resize groups to support column wrapping.
                    var resizeGroups = function () {
                        $('.listGroupContent').each(function (index) {
                            var groupList = this;
                            var childElementHeight = $(groupList.children[0]).outerHeight(true); // Height of a single unit in the group.
                            var availableHeight = $(groupList).closest('.scrollRegion').height() - $(groupList).siblings('.listGroupTitle').outerHeight(); // Available height for the group.

                            var numberOfElementsPerColumn = Math.floor(availableHeight / childElementHeight); // Number of units in the group that can be fit in 1 column.
                            var numberOfColumns = Math.ceil(groupList.childElementCount / numberOfElementsPerColumn); // Total number of columns to fit all units.
                            var widthOfSingleColumn = $(groupList.children[0]).outerWidth(true); // Width of a column.

                            // Set width and height for the group.
                            $(groupList).width(widthOfSingleColumn * numberOfColumns);
                            $(groupList).height(childElementHeight * numberOfElementsPerColumn);
                        });
                    };

                    var timeout;
                    this._throttleOnResize = function () {
                        clearTimeout(timeout);
                        timeout = setTimeout(resizeGroups, 200);
                    };
                    // Throttle group resize when window resizes.
                    $(window).resize(this._throttleOnResize);

                    if ($(instance).is(":hidden")) {
                        setTimeout(resizeGroups, 200);
                    } else {
                        resizeGroups();
                    }
                }
                this._renderedGroupBy = !!groupBy;
                if (isFastEdit) {
                    //Handles first row deletion if that has pending rows pivoted against
                    if (!hasCollection && this._deletedPendingRows) {
                        var firstId = undefined;
                        var itemArray = [];
                        for (var i in this._deletedPendingRows) {
                            item = this._deletedPendingRows[i];
                            item.isFromKeyDown = false;
                            itemArray.push(item);
                            if (firstId === undefined) {
                                firstId = item.id;
                            }
                            this._itemTask && this._itemTask.start(arrayIndex);
                        }
                        this._firstRowDeleted = true;
                        this._dataProvider._firstRowDeleted = true;
                        this._listContext.DataSource(itemArray);
                        this._dataProvider.InternalSelectedRowId(firstId);
                    }
                }
                //  7) Other post render stuff
                // Infinite scroll post render related logic
                this._postRenderAll(true, restrictedRendering, wasUpdatingSet, layoutBlockSet, postRender);

                this._cleanupRenderAll(startTime);

                return { changeSkipped: changeSkipped };
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Updates client cache start based on rendering mode: merge/replace.
        * @param {Boolean} mergeMode - Merge mode indicates whether rendering algorithm should preserve all previously rendered rows
        * that are outside of current page. There are two modes: replace and merge.
        * @param {Boolean} mergeAbove - Represents the way of merging: above or below (appropriate for merge rendering mode only)
        */
        _updateClientCacheStart: function (mergeMode, mergeAbove) {
            var serverCachePageStart = $dyn.peek(this._dataProvider.FetchedDataStartPosition);
            var dataSourceLength = $dyn.peek(this._dataProvider.DataSource).length;
            if (dataSourceLength) {
                if (mergeMode) {
                    // Update client cache start that represents server cache based index of the first rendered row
                    if (mergeAbove) {
                        this._listContext.UpdateClientCacheStart(serverCachePageStart);
                    }
                } else {
                    this._listContext.UpdateClientCacheStart(serverCachePageStart);
                }
            } else {
                if (!mergeMode) {
                    this._listContext.UpdateClientCacheStart(undefined);
                }
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Helper function for _renderAll. Performs post rendering operations to adjust some Grid state
        * @param {Boolean} domStateChanged - Indicates whether main rendering process changed dom state
        * @param {Boolean} restrictedRendering - Indicates whether rendering happened in the restricted mode
        * @param {Boolean} wasUpdatingSet - Indicates whether updating flag was set while going through main rendering procedure
        * @param {Boolean} layoutBlockSet - Indicates whether layout block was set while going through main rendering procedure
        * @param {Function} postRender - Post render callback
        */
        _postRenderAll: function (domStateChanged, restrictedRendering, wasUpdatingSet, layoutBlockSet, postRender) {
            var that = this;
            // Infinite scroll post render related logic
            this._listContext._readjustScrollState();

            if (domStateChanged) {
                if ($dyn.value(this._listContext.InfiniteScroll)) {
                    // In case rendering produced too many rows, we have to discard some rows
                    if (this._runDiscardPolicy(this._listBody)) {
                        // In case we discarded rows, we need to readjust scroll state again
                        this._listContext._readjustScrollState();
                    }
                }

                // Update order property on every row
                // It should be done after running discard policy as discard policy may remove rows
                this._updateRenderedRowsIndexes();

                if ($dyn.value(this._listContext.InfiniteScroll)) {
                    this._listContext._ensureFullViewPortContent();
                }

                // Update white space width to match the rendered dimension
                this._listContext._updateWhiteSpaceWidth();

                var postRenderStart = performance.now();
                $dyn.applyFunction(postRender);

                var restoreFocusStart = performance.now();

                if (!this.isUserSelectedRowRendered()) {
                    this._dataProvider.syncInternalSelectedRowToDataSelectedRow();
                }

                var userSelectedElement = this.getUserSelectedElement();
                if (userSelectedElement && this._selectedRowId !== this._selectingRowId) {
                    userSelectedElement.setAttribute(this._rowSelecting, true);
                }

                // Do not update focus to grid if rendering list on the task recorder pane, keep focus on the user's selected form
                if(!this._listContext.Root || this._listContext.Root.FormName !== 'SysTaskRecorderPane') {
                    if ($dyn.value(this._listContext.InfiniteScroll)) {
                        this.restoreSelectionFocus();
                    } else {
                        this.focusCellInActiveRow();
                    }
                }
            }

            if (wasUpdatingSet) {
                $dyn.ui.updating(false);
            }

            if (!restrictedRendering) {
                // Need to queue the parent element for an arrange
                $dyn.layout.queueArrange(this._listBody.parentNode);
            }

            if (!layoutBlockSet) {
                $dyn.ui.layout(false);
            }

            this._forceRender = false;

            // It's possible for the grid to render with a selected item out of view without
            // user interaction (eg, form open, setPosition(), etc), scroll it into view if necessary
            // This has to be done after the surrounding elements are laid out already so the grid has
            // its final dimensions and we can correctly determine if the row is in view
            // We are using setTimeout instead of $dyn.async as scrolling active item into view doesn't change the state of the grid
            if (domStateChanged && !$dyn.peek(this._listContext._dataLoadedRenderPending) && this._listContext._selectedRow.parentNode === this._listBody) {
                this._listContext.setTimeout(function () {
                    that._listContext.scrollItemIntoView(that._listContext._selectedRow);
                });
            }

            // Fix for row jump issue when scrolling reaches its endpoint on a infinite scroll grid.
            // doNotScrollIntoView should be set to true where the scrolling is not desired.
            if ($dyn.value(this._listContext.InfiniteScroll) && !this._listContext.doNotScrollIntoView) {
                this._listContext.setTimeout(function () {
                    //Scrolling upwards
                    if (!that._listContext._scrollParameters.bufferAboveHeight && that._listContext.mergeAbove) {
                        if (that._listContext.clientLastStartPosition) {
                            var renderedRow = that._rowIdToRenderedRow[that._listContext.clientLastStartPosition - 1];
                            if (renderedRow && renderedRow.element) {
                                that._listContext.SetScrollRegionTop(renderedRow.element.offsetTop);
                            }
                            that._listContext.clientLastStartPosition = undefined;
                        }
                    }
                    //Scrolling downwards
                    else if (!that._listContext._scrollParameters.bufferBelowHeight && that._listContext.mergeBelow) {
                        if (that._listContext.clientLastEndPosition) {
                            var noOfRows = that._listContext.CalculateNumberOfVisibleRows();
                            var renderedRow = that._rowIdToRenderedRow[that._listContext.clientLastEndPosition - noOfRows + 1];
                            if (renderedRow && renderedRow.element) {
                                that._listContext.SetScrollRegionTop(renderedRow.element.offsetTop);
                            }
                            that._listContext.clientLastEndPosition = undefined;
                        }
                    }
                });
            }

            if (this._listContext.doNotScrollIntoView) {
                this._listContext.doNotScrollIntoView = false;
                this._listContext.clientLastEndPosition = undefined;
            }
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Helper function for _renderAll. Cleans up the rendering procedure
        * @param {Number} startTime - The time when rendering procedure started
        */
        _cleanupRenderAll: function (startTime) {
            // Any data loaded is now rendered
            this._listContext._dataLoadedRenderPending(false);

            // Render was triggered so grid is displayed
            this._listContext._renderTriggeredWhileHidden = false;

            this._listContext._skipOffsetParentCheck = false;
            this._isRendering = false;

            this._renderForEachTask && this._renderForEachTask.stop();

            var name = '<unknown>';
            var context = $dyn.context(this._listBody);
            if (context && context.Name) {
                name = context.Name;
            }
            var endTime = performance.now();
            $dyn.log.list && $dyn.log.list("List: Finished rendering. Client state: CacheStart: %i, RowCount: %i", this._listContext.clientCacheStart, this.getRenderedRowCount());
            $dyn.log.list && $dyn.log.list("List: for each render time: %s, %s (ms)", name, (endTime - startTime));
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Identifies rendering merge direction (above, below) and provides the anchor element
        * @returns {Object} An object that represents the rendering merge direction and anchor elemnt and anchor id that are used to identify where to place newly rendered rows that are being merged with old rendered rows
        */
        _renderingMergeDirection: function () {
            var mergeBelow = false;
            var mergeAbove = false;
            var insertAfterElement = null;
            var insertAfterId = null;
            var firstRowItem = this._dataProvider.getFirstItem();

            // If we know the scrolling direction use that, otherwise decide by what is already rendered
            if (this._listContext.prevScrollDirection === $dyn.ui.ScrollDirection.down) {
                // The new collection is going to be merged below exsting set of rows
                mergeBelow = true;
            } else if (this._listContext.prevScrollDirection === $dyn.ui.ScrollDirection.up) {
                // The new collection is going to be merged above exsting set of rows
                // No need to update currentInsertAfterElement, as we will be pushing new rows to the top of the grid
                mergeAbove = true;
            } else { //$dyn.ui.ScrollDirection.none
                if (firstRowItem && this._rowIdToRenderedRow[firstRowItem.id]) {
                    mergeBelow = true;
                }

                var lastRowItem = this._dataProvider.getLastItem();
                if (lastRowItem && this._rowIdToRenderedRow[lastRowItem.id]) {
                    mergeAbove = true;
                }
            }

            if (mergeBelow) {
                // Let's get first row item from data provider's current collection
                // And try to find existing row in already rendered set of rows
                var foundRenderedRow = firstRowItem ? this._rowIdToRenderedRow[firstRowItem.id] : undefined;
                // We will be inserting new rows after the following element:
                var renderedRowBefore = foundRenderedRow ? this._getRenderedRowByIndex(foundRenderedRow.order - 1) : undefined;
                if (renderedRowBefore) {
                    insertAfterElement = renderedRowBefore.element;
                    insertAfterId = renderedRowBefore.id;
                } else {
                    // The case when found rendered row is the first rendered row
                    insertAfterElement = null;
                    insertAfterId = null;
                }
            }

            this._listContext.prevScrollDirection = $dyn.ui.ScrollDirection.none;
            this._listContext.mergeAbove = mergeAbove;
            this._listContext.mergeBelow = mergeBelow;
            return {
                mergeAbove: mergeAbove,
                mergeBelow: mergeBelow,
                insertAfterId: insertAfterId,
                insertAfterElement: insertAfterElement,
            };
        },

        /**
        * @function
        * @memberof $dyn.controls.List.Renderer#
        * @private
        * @summary Disposes the renderer
        */
        dispose: function () {
            for (var i = 0; i < this._disposables.length; ++i) {
                var disposable = this._disposables[i];
                if (disposable && disposable.dispose && typeof(disposable.dispose) === "function") {
                    disposable.dispose();
                }
            }
            if (this._dataProvider) {
                this._dataProvider.onRowBecameCommitted.unsubscribe(this._onRowBecameCommittedSubscription);
            }
            if (this._throttleOnResize) {
                $(window).off('resize', this._throttleOnResize);
                this._throttleOnResize = null;
            }
            if (this._layoutSubscriptionId) {
                $dyn.ui.layout.valueChanged.unsubscribe(this._layoutSubscriptionId);
                this._layoutSubscriptionId = null;
            }
        },
    };

})();